import React, { useMemo, useState } from 'react';

import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import vars from 'src/assets/styles/utils/vars.module.scss';
import { AssetBlock } from 'src/components/AssetBlock';
import { AssetListItem } from 'src/components/AssetListItem';
import { EExportStatus } from 'src/components/AssetsEnergyExportIndicator';
import { BaseIcon } from 'src/components/BaseIcon';
import { BaseList } from 'src/components/BaseList';
import { BaseSearch } from 'src/components/BaseSearch';
import { BaseSwitch, TBaseSwitchProps } from 'src/components/BaseSwitch';
import { BaseTooltipTrigger } from 'src/components/BaseTooltip';
import { CustomScrollbar } from 'src/components/CustomScrollbar';
import { LIBRARY_FILTERS_MAPPING } from 'src/constants/application';
import {
  FILTER_BUTTONS,
  MAX_ASSETS_GUEST,
  MAX_ASSETS_LOGGED_IN,
} from 'src/features/Assets/ListAssets/ListAssetsWithSearch/constants';
import { setModalAssetManagerShowGuide } from 'src/redux/application/application.slice';
import { selectIsLoggedIn } from 'src/redux/auth/auth.selectors';
import { selectSimulationResults } from 'src/redux/configuration/configuration.selectors';
import { useAppDispatch } from 'src/redux/store';
import { getLibraryBadgeColor } from 'src/utils/getLibraryBadgeColor';

import s from './AssetsListWithSearch.module.scss';
import {
  AssetsListActiveFilter,
  TAssetsListWithSearchProps,
  TFilterItem,
  TListView,
} from './AssetsListWithSearch.types';

export const AssetsListWithSearch: React.FC<TAssetsListWithSearchProps> = ({
  assets,
  searchQuery,
  activeFilter,
  disabledFilters,
  isAllButtonRequiredInFilter,
  onSearchQueryChange,
  onFilterChange,
  onAssetChoose,
  loading,
  error,
  showGuide,
  noResultsMessage = 'No results.',
  paddingClassName,
  onThreeDotsClick,
  theme = 'dark',
  showEnergyStatus = false,
  showAssetsCount = false,
  listViewProp = 'grid',
  searchPosition = 'bottom',
  showCustomAssetFilter = false,
  disableDescription = false,
  disableAssetDescription = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [listView, setListView] = useState<TListView>(listViewProp);
  const simulationResults = useSelector(selectSimulationResults);
  const isLoggedIn = useSelector(selectIsLoggedIn);

  const handleViewChange: TBaseSwitchProps['onChange'] = ({ value }) => {
    setListView(value as TListView);
    dispatch(setModalAssetManagerShowGuide(false));
  };

  const filterButtons = useMemo<Array<TFilterItem>>(() => {
    const tempFilterButtons = [...FILTER_BUTTONS];
    if (!showCustomAssetFilter) return tempFilterButtons;

    tempFilterButtons.splice(1, 0, {
      icon: 'star',
      label: 'Custom',
      value: LIBRARY_FILTERS_MAPPING.Custom,
    });

    return tempFilterButtons;
  }, [showCustomAssetFilter]);

  const selectDescription = () => {
    switch (activeFilter?.[0]) {
      case AssetsListActiveFilter.HOME:
        return { title: t('common.HOME'), description: t('tooltips.HOME_INFO') };
      case AssetsListActiveFilter.PV:
        return {
          title: t('common.SOLAR_PANELS'),
          description: '',
        };
      case AssetsListActiveFilter.LOAD:
        return { title: t('common.CONSUMPTION'), description: t('tooltips.LOAD_INFO') };
      case AssetsListActiveFilter.STORAGE:
        return {
          title: t('common.BATTERY'),
          description: t('tooltips.BATTERY_INFO'),
        };
      case AssetsListActiveFilter.HEATPUMP:
        return {
          title: t('common.HEAT_PUMP'),
          description: null,
        };
      default:
        return { title: t('common.ALL'), description: t('messages.CHOOSE_YOUR_ASSETS') };
    }
  };

  const getEnergyStatus = (key: string): EExportStatus | undefined => {
    if (!simulationResults) {
      return undefined;
    }
    if (simulationResults.bills && simulationResults.bills[key]) {
      const { total_energy } = simulationResults.bills[key];
      return total_energy < 0 ? EExportStatus.Exporter : EExportStatus.Importer;
    }
    return undefined;
  };

  const { title, description } = selectDescription();

  return (
    <>
      <div>
        {showAssetsCount && assets.length > 0 && (
          <p className={s.totalAssets}>
            {assets.length}/{isLoggedIn ? MAX_ASSETS_LOGGED_IN : MAX_ASSETS_GUEST}
          </p>
        )}
        {searchPosition === 'top' && (
          <div className={classnames(s.searchWrapper, paddingClassName)}>
            <BaseSearch
              name="assetsSearch"
              value={searchQuery}
              onChange={({ value }) => onSearchQueryChange(value)}
              theme={theme === 'dark' ? 'filled-dark' : 'filled-gray'}
              label=""
              iconRight="search"
              inputHeight="2"
              iconRightSize={12}
              className={s.search}
              autoComplete={undefined}
              iconRightColor={vars['color-black']}
            />
          </div>
        )}
        <div className={classnames(s.filtersWrapper, paddingClassName)}>
          {filterButtons.map((item, index) => {
            if (isAllButtonRequiredInFilter && item.label === 'All') {
              return;
            } else {
              return (
                <BaseTooltipTrigger
                  key={index}
                  triggerClassName={classnames(s.filterBtnWrapper, {
                    [s.filterBtnLarge]: !index && item.label === 'All',
                    [s.filterBtnActive]: activeFilter === item.value,
                    [s['theme-' + theme]]: true,
                  })}
                  position="top"
                  disabled={!item.icon}
                  tooltipChildren={item.label}
                  wordWrap={true}>
                  <button
                    type="button"
                    disabled={disabledFilters?.includes(item.value)}
                    onClick={() => onFilterChange(item)}>
                    {item.icon ? <BaseIcon icon={item.icon} size={11} /> : item.label}
                  </button>
                </BaseTooltipTrigger>
              );
            }
          })}
          {searchPosition === 'top' && (
            <BaseSwitch
              name="assets-view"
              options={[
                {
                  icon: 'table',
                  value: 'list' as const,
                },
                {
                  icon: 'grid',
                  value: listViewProp ? listViewProp : ('grid' as const),
                },
              ]}
              value={listView}
              onChange={handleViewChange}
              theme={theme}
              variant="horizontal-small"
            />
          )}
        </div>
        {searchPosition === 'bottom' && (
          <div className={classnames(s.searchWrapper, paddingClassName)}>
            <BaseSearch
              name="assetsSearch"
              value={searchQuery}
              onChange={({ value }) => onSearchQueryChange(value)}
              theme={theme === 'dark' ? 'filled-dark' : 'filled-gray'}
              label=""
              iconRight="search"
              inputHeight="2"
              iconRightSize={12}
              className={s.search}
              autoComplete={undefined}
              iconRightColor={vars['color-black']}
            />
            <BaseSwitch
              name="assets-view"
              options={[
                {
                  icon: 'table',
                  value: 'list' as const,
                },
                {
                  icon: 'grid',
                  value: listViewProp ? listViewProp : ('grid' as const),
                },
              ]}
              value={listView}
              onChange={handleViewChange}
              theme={theme}
              variant="horizontal-small"
            />
          </div>
        )}
      </div>

      <CustomScrollbar className={classnames(s.assetsListWrapper, paddingClassName)}>
        {!loading && (
          <div
            className={classnames(
              s.boxDescription,
              theme === 'light' ? s.boxDescriptionLight : s.boxDescriptionDark,
              { [s.hideDescription]: disableDescription },
            )}>
            <h3>{title}</h3>
            <p>{description}</p>
          </div>
        )}

        {loading ? (
          <p className={s.statusMessage}>Loading...</p>
        ) : error ? (
          <p className={s.statusMessage}>Error: {error}</p>
        ) : assets.length ? (
          <BaseList className={s[`assets-ul-${listView}`]}>
            {assets.map((asset, index) => {
              if (!asset) return null;

              const Block =
                listView === 'grid' || listView === '3grid' ? (
                  <AssetBlock
                    theme={theme}
                    icon={asset.icon}
                    title={asset.title}
                    subtitle={disableAssetDescription ? '' : asset.subtitle}
                    description={asset.description}
                    onClick={
                      onAssetChoose && !asset.disabled ? () => onAssetChoose(asset) : undefined
                    }
                    onThreeDotsClick={
                      onThreeDotsClick
                        ? (e) => onThreeDotsClick({ target: e.target as HTMLElement, asset })
                        : undefined
                    }
                  />
                ) : (
                  <AssetListItem
                    energyExportStatus={showEnergyStatus ? getEnergyStatus(asset.title) : undefined}
                    theme={theme}
                    icon={asset.icon}
                    title={asset.title}
                    subtitle={asset.subtitle}
                    badgeColor={getLibraryBadgeColor(asset)}
                    onClick={onAssetChoose ? () => onAssetChoose(asset) : undefined}
                    onThreeDotsClick={
                      onThreeDotsClick
                        ? (e) => onThreeDotsClick({ target: e.target as HTMLElement, asset })
                        : undefined
                    }
                  />
                );

              if (index === 0) {
                return (
                  <div
                    key={asset.key}
                    className={classnames(s.blockWrapper, {
                      [s.blockHintActive]: showGuide || (asset.type === 'PV' && asset.isCustomPV),
                    })}>
                    {Block}
                  </div>
                );
              }

              return <React.Fragment key={asset.key}>{Block}</React.Fragment>;
            })}
          </BaseList>
        ) : (
          <p className={s.statusMessage}>{noResultsMessage}</p>
        )}
      </CustomScrollbar>
    </>
  );
};
